import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api';
import { planEndpoints } from '../api-plans';
import type { InstallmentPlan } from '../api-plans';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel, Table } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars, humanize } from './event/shared';

/**
 * Installment plans (D-355): what a package offers, on the package, and a
 * plan's schedule, on the order.
 */

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const chargeTone = (status: string): Tone => (status === 'PAID' || status === 'COVERED' ? 'ok' : status === 'FAILED' ? 'warn' : status === 'DEFAULTED' ? 'bad' : 'neutral');
const planTone = (status: string): Tone => (status === 'ACTIVE' ? 'accent' : status === 'COMPLETED' ? 'ok' : status === 'DEFAULTED' ? 'bad' : 'neutral');

/** On a package: offer it in monthly installments on the web, or not. */
export function InstallmentTermsPanel({ packageId, isPass, manage, onError }: { packageId: string; isPass: boolean; manage: boolean; onError: (message: string) => void }) {
  const queryClient = useQueryClient();
  const terms = useQuery({ queryKey: ['packages', packageId, 'installments'], queryFn: () => planEndpoints.packageTerms(packageId) });
  const [count, setCount] = useState('3');
  const [web, setWeb] = useState(true);
  useEffect(() => {
    const current = terms.data?.installments;
    if (current) {
      setCount(String(current.installment_count));
      setWeb(current.offered_on_web);
    }
  }, [terms.data]);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['packages', packageId, 'installments'] });
  const save = useMutation({ mutationFn: () => planEndpoints.setPackageTerms(packageId, { installment_count: Number(count), interval: 'MONTH', offered_on_web: web }), onSuccess: refresh, onError: (cause: ApiError) => onError(cause.message) });
  const remove = useMutation({ mutationFn: () => planEndpoints.removePackageTerms(packageId), onSuccess: refresh, onError: (cause: ApiError) => onError(cause.message) });
  if (isPass) return null;
  const current = terms.data?.installments ?? null;
  return (
    <Panel
      title="Installments"
      description="Let buyers pay in equal monthly installments: the first at checkout, the rest charged to the card they save, with their consent recorded on the order. A missed payment is asked for by link and staff are alerted; a refund stops the plan. Plans already sold are not changed by editing this."
      actions={
        manage ? (
          <div className="flex flex-wrap gap-2">
            {current ? <Button disabled={remove.isPending} onClick={() => remove.mutate()}>Stop offering</Button> : null}
            <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>{current ? 'Save' : 'Offer installments'}</Button>
          </div>
        ) : null
      }
    >
      <div className="flex flex-wrap items-end gap-4 px-5 py-4">
        <Field label="Installments" htmlFor="plan-count" hint="Monthly, 2 to 12. The odd cents go on the last one.">
          <select id="plan-count" value={count} onChange={(e) => setCount(e.target.value)} className={INPUT} disabled={!manage}>
            {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => (
              <option key={n} value={n}>
                {n} monthly payments
              </option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={web} onChange={(e) => setWeb(e.target.checked)} disabled={!manage} />
          Offered on the website
        </label>
        <p className="text-sm text-ink-600">{current ? `Offered: ${current.installment_count} monthly payments${current.offered_on_web ? ' on the web' : ', not on the web'}.` : 'Not offered: buyers pay in full.'}</p>
      </div>
    </Panel>
  );
}

/** On an order: the plan's schedule, what was charged, what failed, and Stop. */
export function InstallmentPlanPanel({ orderId, onNotice }: { orderId: string; onNotice: (message: string) => void }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const plan = useQuery({ queryKey: ['orders', orderId, 'installments'], queryFn: () => planEndpoints.orderPlan(orderId) });
  const stop = useMutation({
    mutationFn: () => planEndpoints.stopOrderPlan(orderId, reason),
    onSuccess: () => {
      setReason('');
      setError(null);
      onNotice('The plan is stopped: nothing more will be charged. Any balance is still owed.');
      void queryClient.invalidateQueries({ queryKey: ['orders', orderId] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const p: InstallmentPlan | null = plan.data?.installment_plan ?? null;
  if (!p) return null;
  return (
    <Panel title="Installment plan" description={`${p.installment_count} monthly payments of a ${dollars(p.total_cents)} order. Charged so far ${dollars(p.charged_cents)}; still scheduled ${dollars(p.scheduled_cents)}.${p.card_saved ? '' : ' No card is saved: each payment goes out as a link.'}`}>
      <div className="space-y-3 px-5 py-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={planTone(p.status)}>{humanize(p.status)}</Badge>
          {p.stopped_at ? <span className="text-ink-600">Stopped {day(p.stopped_at)}: {p.stop_reason}</span> : null}
          {p.defaulted_at ? <span className="text-ink-600">Defaulted {day(p.defaulted_at)}. The tickets do not admit until the balance is paid.</span> : null}
          {p.completed_at ? <span className="text-ink-600">Paid in full {day(p.completed_at)}.</span> : null}
        </div>
        {error ? <ErrorNote>{error}</ErrorNote> : null}
      </div>
      <Table headers={['#', 'Due', 'Amount', 'Charged', 'Status', 'Notes']}>
        {p.charges.map((c) => (
          <tr key={c.sequence} className="border-b border-line">
            <td className="px-5 py-2 text-sm">{c.sequence}</td>
            <td className="px-5 py-2 text-sm whitespace-nowrap">{day(c.due_at)}</td>
            <td className="tabular px-5 py-2 text-sm">{dollars(c.amount_cents)}</td>
            <td className="tabular px-5 py-2 text-sm">{c.charged_cents === null ? '' : dollars(c.charged_cents)}</td>
            <td className="px-5 py-2 text-sm">
              <Badge tone={chargeTone(c.status)}>{humanize(c.status)}</Badge>
            </td>
            <td className="px-5 py-2 text-sm text-ink-600">
              {c.attempt_count > 1 ? `${c.attempt_count} tries. ` : ''}
              {c.last_failure_message ?? ''}
              {c.next_attempt_at && c.status === 'FAILED' ? ` Next try ${day(c.next_attempt_at)}.` : ''}
            </td>
          </tr>
        ))}
      </Table>
      <div className="space-y-2 px-5 py-4">
        <p className="text-xs text-ink-400">
          What the customer agreed to, {day(p.consented_at)}: “{p.consent_text}”
        </p>
        {p.status === 'ACTIVE' && can('orders.payments.collect') ? (
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Stop the plan" htmlFor="plan-stop" hint="No refund: the remaining balance stays owed, to be collected another way.">
              <input id="plan-stop" value={reason} onChange={(e) => setReason(e.target.value)} className={`${INPUT} w-72`} placeholder="Why" />
            </Field>
            <Button disabled={!reason.trim() || stop.isPending} onClick={() => stop.mutate()}>
              Stop charging
            </Button>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
